import type { ValidationError } from "yup";

export type ErrorMap = Record<string, string>;

/**
 * Normalizes Yup's path format "positions[0].service.id"
 * into something your frontend can consume directly.
 */
const normalizePath = (path?: string | null): string | null => {
	if (path == null) return null;

	// Convert array indices from "positions[0].service" → "positions.0.service"
	return path.replace(/\[(\d+)\]/g, ".$1");
};

/**
 * Converts Yup ValidationError into your ErrorMap.
 *
 * - Supports nested structures
 * - Supports arrays
 * - Supports sibling errors
 * - Collapses multiple errors per field into a single string
 */
export function mapYupErrorsToErrorMap(err: unknown): ErrorMap {
	const errorMap: ErrorMap = {};

	if (err == null || typeof err !== "object") {
		return errorMap;
	}

	// Handle only Yup validation errors
	if ("inner" in (err as ValidationError)) {
		const yupErr = err as ValidationError;

		// Yup may provide a top-level message without inner[] entries
		if (yupErr.inner == null || yupErr.inner.length === 0) {
			const key = normalizePath(yupErr.path);
			if (key != null) {
				errorMap[key] = yupErr.message;
			}
			return errorMap;
		}

		// Collect all field errors
		for (const innerErr of yupErr.inner) {
			const key = normalizePath(innerErr.path);
			if (key == null) continue;

			// If a field already has an error, append — or overwrite?
			// Here we overwrite intentionally, as the last one is usually most specific.
			errorMap[key] = innerErr.message;
		}

		return errorMap;
	}

	// Not a Yup error → return empty map
	return errorMap;
}
